import { addHours, format } from 'date-fns';
import { getValues } from '@shopify/react-form';
import getRealDate from '@sdk/utils/getRealDate';
import validateTime from '@sdk/utils/validation/validateTime';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';
import { mapPersonaToPickerOption } from '@sdk/components/forms/personaPickers/GroupedPersonaPicker/groupedPersonaPicker.utils';
import { PositionType } from '@sdk/positions/types/PositionType';
import { LocationType } from '@sdk/locations/types/LocationType';

import {
  MaybeClockInClockOut,
  FormFields,
  FieldValues,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';

/**
 * Gets list of available options to present under persona picker select menu for locations.
 */
export const getLocationOptions = (locations: LocationType[]): PersonaPickerOption[] =>
  locations.map(mapPersonaToPickerOption);

/**
 * Gets list of available options to present under persona picker select menu for positions.
 */
export const getPositionOptions = (positions: PositionType[]): PersonaPickerOption[] =>
  positions.map(mapPersonaToPickerOption);

/**
 * This function, when passed the clockIn time that is earlier than clockOut time, will add 24 hours to clockOut date.
 * That logic does not apply though if either one of the boundaries is not provided (clockIn or clockOut).
 * In that case final value for missing variable will become null.
 *
 * If isDateWithTime is true then we make sure that clockIn is no earlier than "date" datetime.
 *
 * @param date Date
 * @param clockIn string
 * @param clockOut string
 * @param isDateWithTime boolean
 * @returns MaybeClockInClockOut
 */
export const getClockInClockOutDates = (
  date: Date,
  clockIn?: string,
  clockOut?: string,
  { isDateWithTime = false } = {}
): MaybeClockInClockOut => {
  const dateString = format(date, 'yyyy-MM-dd');
  let clockInDate = validateTime(clockIn) ? getRealDate(`${dateString}T${clockIn}`) : null;
  let clockOutDate = validateTime(clockOut) ? getRealDate(`${dateString}T${clockOut}`) : null;

  if (clockInDate === null || clockOutDate === null) {
    return {
      clockIn: clockInDate,
      clockOut: clockOutDate,
    };
  }

  if (isDateWithTime && clockInDate <= date) {
    clockInDate = addHours(clockInDate, 24);
  }

  if (clockOutDate.getTime() <= clockInDate.getTime()) {
    clockOutDate = addHours(clockOutDate, 24);
  }

  return {
    clockIn: clockInDate,
    clockOut: clockOutDate,
  };
};

/**
 * Shopify React Form doesn't include dynamic list fields inside it's basic fields set.
 * This small helper function helps extract Create Timesheet Form field values.
 *
 * It's like shopify's `getValues` but with support for timeClock field inside dynamicList.
 *
 * @param formData
 * @returns
 */
export const getFieldValues = (formData: FormFields): FieldValues => ({
  ...getValues(formData.fields),
  timeClock: formData.dynamicLists.timeClock.value,
});
